import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { MINIMUM_ACCEPTED_AUDIO_DURATION } from '@modals/PriceModal/constants';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { resolveOrganizationBillingAccountId } from '@helpers/resolveOrganizationBillingAccountId';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useQuery } from '@tanstack/react-query';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchJobPricingQueryFn } from 'app/queries/billing/createFetchJobPricingQueryFn';
import { createFetchJobSubmissionPaymentMethodsQueryFn } from 'app/queries/billing/createFetchJobSubmissionPaymentMethodsQueryFn';
import { useEffect, useState } from 'react';

import { PriceModalErrors } from './components/errors';
import { PriceModalNoPriceDescription } from './components/noPriceDescription';
import { PriceModalPriceDescription } from './components/priceDescription';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';
import { Button } from '@components/core-ui/form-elements/buttons/Button';

export function PriceModal({
  isSubmitting,
  setPaymentIntent,
  paymentIntent,
  onCancel,
  onSuccess,
  jobId,
  enhancedPaymentVariant,
  freeTrialInEffect,
}: TProps) {
  const { call } = useAPI();
  const { updateMe, organization } = useAuthContext();

  const [dataLoaded, setDataLoaded] = useState(true);
  const [isMeRefreshed, setIsMeRefreshed] = useState(false);
  const [balanceIsRefreshing, setBalanceIsRefreshing] = useState(false);
  const baId = resolveOrganizationBillingAccountId(organization);

  const pricingQuery = useQuery({
    queryKey: billingKeys.jobPricing(jobId, Boolean(enhancedPaymentVariant)),
    queryFn: createFetchJobPricingQueryFn(call),
    refetchOnMount: 'always',
  });
  const paymentMethodsQuery = useQuery({
    queryKey: billingKeys.jobSubmissionPaymentMethodsByAccount(baId ?? ''),
    queryFn: createFetchJobSubmissionPaymentMethodsQueryFn(call),
    enabled: Boolean(baId),
  });
  useNotifyQueryError(pricingQuery.error);
  useNotifyQueryError(paymentMethodsQuery.error);

  const pricing = pricingQuery.data?.result;
  const price = pricing ? { ...pricing, ASRRate: String(pricing.ASRRate) } : { price: 0, ASRRate: '0' };
  const audioDuration = pricing?.duration || 0;
  const paymentMethodId = baId ? paymentMethodsQuery.data?.find((item) => item.default)?.id : undefined;

  const balance = organization?.billingAccount?.currentBalance ?? 0;
  const notEnoughFunds = (price?.price ?? 0) > balance;
  const cannotViewBilling = !organization?.permissions?.viewBilling;
  const noBillingAccount = !baId;
  const isAutoTopUpEnabled = organization?.billingAccount?.AutoTopUpSettings?.enabled;

  useEffect(() => {
    let isCurrent = true;
    setIsMeRefreshed(false);
    void updateMe().finally(() => {
      if (isCurrent) {
        setIsMeRefreshed(true);
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [
    enhancedPaymentVariant,
    jobId,
    organization?.id,
    organization?.billingAccount?.id,
    organization?.billingAccountId,
  ]);

  const loading =
    isSubmitting || !dataLoaded || !isMeRefreshed || pricingQuery.isLoading || paymentMethodsQuery.isLoading;
  const isPricingCurrent = Boolean(pricing) && !pricingQuery.isFetching && !pricingQuery.isError;
  const error = noBillingAccount || (!paymentMethodId && notEnoughFunds) || (notEnoughFunds && !isAutoTopUpEnabled);

  if (loading) {
    return (
      <ModalWrapper width="390px">
        {loading && <AbsoluteSpinner overlay={false} />}
        <ModalTitle>Confirm</ModalTitle>
        <Content></Content>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper width="390px" className="min-h-[100px]" onCancel={onCancel}>
      <ModalTitle>Confirm</ModalTitle>
      <Content>
        <div data-testid="priceDescription">
          {cannotViewBilling ? (
            <PriceModalNoPriceDescription price={price} />
          ) : (
            <PriceModalPriceDescription
              price={price}
              setDataLoaded={setDataLoaded}
              balanceIsRefreshing={balanceIsRefreshing}
              setBalanceIsRefreshing={setBalanceIsRefreshing}
              audioDuration={audioDuration}
              enhancedPaymentVariant={enhancedPaymentVariant}
              freeTrialInEffect={freeTrialInEffect}
            />
          )}
        </div>

        <div className={`errorsWrapper ${error ? 'active' : ''}`}>
          <PriceModalErrors
            setBalanceIsRefreshing={setBalanceIsRefreshing}
            price={price}
            onCancel={onCancel}
            setPaymentIntent={setPaymentIntent}
            paymentIntent={paymentIntent}
            dataLoaded={dataLoaded}
            setDataLoaded={setDataLoaded}
            onSuccess={onSuccess}
            jobId={jobId}
            isSubmitting={isSubmitting}
            paymentMethodId={paymentMethodId}
          />
        </div>

        <div className="buttonWrapper">
          <OutlinedButton className="relative w-full" onClick={onCancel}>
            Cancel
          </OutlinedButton>

          <Button
            className="relative w-full"
            data-testid="priceModalSubmitButton"
            disabled={loading || !isPricingCurrent || audioDuration < MINIMUM_ACCEPTED_AUDIO_DURATION || error}
            onClick={() => {
              setDataLoaded(false);
              onSuccess();
              setTimeout(updateMe, 1000);
            }}
          >
            {enhancedPaymentVariant ? 'Enhance' : 'Transcribe'}
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
}
